import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import { ensureLocaleLoaded } from "@/i18n/locale-loader"
import {
	getStoredLanguage,
	type I18nLanguage,
	supportedI18nLanguages,
} from "@/store/settings-state"
import commonEn from "../locales/en/common.json"

const englishComponentFiles = import.meta.glob<Record<string, unknown>>(
	"../locales/en/components/*.json",
	{
		eager: true,
		import: "default",
	},
)

const englishStoreFiles = import.meta.glob<Record<string, unknown>>("../locales/en/store/*.json", {
	eager: true,
	import: "default",
})

const buildNamespaceMap = (files: Record<string, Record<string, unknown>>) =>
	Object.fromEntries(
		Object.entries(files).map(([path, resource]) => {
			const filename = path.split("/").at(-1)
			if (!filename) {
				throw new Error(`Unable to determine the English resource name for "${path}".`)
			}
			return [filename.replace(/\.json$/, ""), resource]
		}),
	)

const componentsEn = buildNamespaceMap(englishComponentFiles)
const storeEn = buildNamespaceMap(englishStoreFiles)

/**
 * Detect the user's preferred locale from the browser, falling back to 'en'.
 */
export function detectLocale(): string {
	try {
		const lang = navigator.language
		if (lang) {
			return lang
		}
	} catch {
		// SSR or restricted environment
	}
	return "en"
}

/**
 * Load a locale's hashed runtime bundles. English is already embedded in the app.
 */
export const ensureLanguageLoaded = (language: I18nLanguage): Promise<void> =>
	ensureLocaleLoaded(i18n, language)

/**
 * Initialize i18next with English embedded as the guaranteed fallback, then load
 * the selected non-English locale from its content-hashed runtime bundles.
 */
export async function initI18n(
	enabledLanguages: readonly I18nLanguage[] = supportedI18nLanguages,
): Promise<typeof i18n> {
	const storedLanguage = getStoredLanguage()
	const selectedLanguage = enabledLanguages.includes(storedLanguage) ? storedLanguage : "en"

	await i18n.use(initReactI18next).init({
		lng: "en",
		fallbackLng: "en",
		returnEmptyString: false,
		supportedLngs: [...supportedI18nLanguages],
		load: "currentOnly",
		ns: ["common", "components", "store"],
		defaultNS: "common",
		resources: {
			en: {
				common: commonEn,
				components: componentsEn,
				store: storeEn,
			},
		},
		interpolation: {
			escapeValue: false, // React already escapes output
		},
		react: {
			useSuspense: false,
		},
	})

	if (selectedLanguage !== "en") {
		try {
			await ensureLanguageLoaded(selectedLanguage)
			await i18n.changeLanguage(selectedLanguage)
		} catch (error) {
			console.error(
				`Failed to load locale "${selectedLanguage}". Falling back to English.`,
				error,
			)
		}
	}

	return i18n
}

export default i18n
